import { Alert, Button, Form, FormButtonRow, FormErrorMessage, FormGroup, Text } from '@ksyos/design-system';
import { useState } from 'react';
import type { FundusScreeningManualImportFrontendSchemaValidated } from '@common/validation/fundusScreening';
import type { FormResult } from '@frontend/utils/hooks/useForm';
import { ToastType } from '@common/enums/toastType';
import { fundusScreeningManualImportFrontendSchema } from '@common/validation/fundusScreening';
import { FormProvider } from '@frontend/components/contexts/form/FormProvider';
import { Dropzone } from '@frontend/components/elements/dropzone/Dropzone';
import { DropzoneFile } from '@frontend/components/elements/dropzone/DropzoneFile';
import { fundusScreeningManualImportApi } from '@frontend/services/api/fundusScreeningApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { mapExtensionsToMimeTypes } from '@frontend/utils/fileTypeUtils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { showToast } from '@frontend/utils/showToast';
import {
    FundusScreeningManualImportErrorMessages,
    type FundusScreeningManualImportErrors,
} from '../FundusScreeningManualImportErrorMessages';
import { SelectThirdParty } from './SelectThirdParty';

export function FundusScreeningManualImportForm() {
    const abortSignal = useAbortSignal();
    const [apiErrors, setApiErrors] = useState<FundusScreeningManualImportErrors & { hasErrors: boolean }>({
        excelParserErrors: [],
        hasErrors: false,
    });

    const translate = getSelectedLanguage();

    return (
        <FormProvider
            initialValues={{ file: null, isInvalidFile: false, thirdParty: null }}
            onSubmit={handleSubmit}
            validationSchema={fundusScreeningManualImportFrontendSchema}
        >
            {(form) => (
                <Form
                    onReset={form.handleReset}
                    onSubmit={form.handleSubmit}
                    submitOnEnter={false}
                >
                    <SelectThirdParty />
                    <FormGroup
                        hasErrors={apiErrors.hasErrors || form.errors.has('file')}
                        isRequired={true}
                        label={translate.fundusScreening.manualImport.form.callingFile}
                    >
                        {form.values.file ? (
                            <DropzoneFile
                                filename={form.values.file.name}
                                key={form.values.file.name}
                                mimeType={form.values.file.type}
                                onFileRemove={() => form.handleChange({ name: 'file', value: null })}
                                onThumbnailClick={() => {}}
                                thumbnailUrl={undefined}
                            />
                        ) : (
                            <Dropzone
                                acceptedMimeTypes={mapExtensionsToMimeTypes('.xls, .xlsx')}
                                files={[]}
                                maxFiles={1}
                                onFileRemove={() => form.handleChange({ name: 'file', value: null })}
                                onFilesAdded={(files) => {
                                    handleFileUpload(form, files);
                                }}
                                onFilesRejected={() => {
                                    form.handleChange({ name: 'isInvalidFile', value: true });
                                }}
                                onThumbnailClick={() => {}}
                            />
                        )}
                        {form.errors.has('file') && !form.values.isInvalidFile && (
                            <FormErrorMessage>{form.errors.getMessage('file')}</FormErrorMessage>
                        )}
                    </FormGroup>
                    {form.values.isInvalidFile ? (
                        <Alert appearance="warning">
                            <Text
                                color="warning"
                                fontWeight="bold"
                            >
                                {translate.validation.excelParser.invalidFileTitle}
                            </Text>
                            <Text color="warning">{translate.validation.excelParser.invalidFileText}</Text>
                        </Alert>
                    ) : (
                        apiErrors.hasErrors && (
                            <FundusScreeningManualImportErrorMessages excelParserErrors={apiErrors.excelParserErrors} />
                        )
                    )}
                    <FormButtonRow>
                        <Button
                            hidden={form.isSubmitting}
                            type="reset"
                            variant="outline"
                        >
                            {translate.fundusScreening.manualImport.form.resetButtonLabel}
                        </Button>
                        <Button
                            isLoading={form.isSubmitting}
                            type="submit"
                            variant="solid"
                        >
                            {translate.fundusScreening.manualImport.form.submitButtonLabel}
                        </Button>
                    </FormButtonRow>
                </Form>
            )}
        </FormProvider>
    );

    function handleFileUpload(
        form: FormResult<FundusScreeningManualImportFrontendSchemaValidated>,
        files: FundusScreeningManualImportFrontendSchemaValidated['file'][]
    ) {
        if (files.length === 0) {
            form.handleChange([
                { name: 'file', value: null },
                { name: 'isInvalidFile', value: false },
            ]);

            return;
        }
        form.handleChange([
            { name: 'file', value: files[0] },
            { name: 'isInvalidFile', value: false },
        ]);
    }

    async function handleSubmit({ file, thirdParty }: FundusScreeningManualImportFrontendSchemaValidated) {
        const result = await fundusScreeningManualImportApi(thirdParty, file, abortSignal);

        if (result.success) {
            showToast(ToastType.success, translate.fundusScreening.manualImport.successfulImport);
        }

        setApiErrors({
            excelParserErrors: result.excelParser.errors,
            hasErrors: !result.success,
        });
    }
}
